import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../ui/sheet";
import { Menu, Snowflake } from "lucide-react";
import { navLinks, siteInfo } from "../../data/mock";

export const Navbar = ({ siteContent }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const businessName = siteContent?.business_name || siteInfo.name;
  const whatsappNumber = siteContent?.whatsapp_number || siteInfo.whatsappNumber;
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hi Frostyware 3D! I'm interested in your 3D printing services.")}`;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setOpen(false);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl shadow-sm border-b border-sky-100/50"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <div
            className="flex items-center gap-2.5 cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 to-cyan-300 flex items-center justify-center shadow-md shadow-sky-200/50">
              <Snowflake className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-slate-900 tracking-tight">
              {businessName}
            </span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-sm font-medium text-slate-600 hover:text-sky-600 transition-colors duration-200 relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-sky-500 after:transition-all after:duration-300 hover:after:w-full"
              >
                {link.label}
              </button>
            ))}
            <Link
              to="/blog"
              data-testid="navbar-blog-link"
              className="text-sm font-medium text-slate-600 hover:text-sky-600 transition-colors duration-200 relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-sky-500 after:transition-all after:duration-300 hover:after:w-full"
            >
              Blog
            </Link>
            <Button
              onClick={() => window.open(whatsappLink, "_blank")}
              className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-6 shadow-md shadow-sky-200/50 hover:shadow-lg hover:shadow-sky-200/60 transition-all duration-300"
            >
              Order Now
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="text-slate-700">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-white/95 backdrop-blur-xl">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-2 mt-8">
                <div className="flex items-center gap-2.5 mb-6">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-cyan-300 flex items-center justify-center">
                    <Snowflake className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-base font-bold text-slate-900">
                    {businessName}
                  </span>
                </div>
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="text-left text-base font-medium text-slate-700 hover:text-sky-600 transition-colors py-3 px-3 rounded-lg hover:bg-sky-50"
                  >
                    {link.label}
                  </button>
                ))}
                <Link
                  to="/blog"
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-slate-700 hover:text-sky-600 transition-colors py-3 px-3 rounded-lg hover:bg-sky-50"
                >
                  Blog
                </Link>
                <Button
                  onClick={() => {
                    setOpen(false);
                    window.open(whatsappLink, "_blank");
                  }}
                  className="bg-sky-500 hover:bg-sky-600 text-white rounded-full mt-4"
                >
                  Order on WhatsApp
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
